import {
    centsToFormattedDollars,
    formattedDollarsToCents,
} from '@fruitful/fruitils';
import {
    isFruitfulAccount,
    removeTransferAccountsFromRulesAccounts,
    syncIncomeMoneyMapAccountToRulesSharingAccountId,
} from '@fruitful/money-map';
import {
    AccountOwnership,
    type DeepPartial,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountWithAccountDetails,
    MoneyMapCategory,
    type MoneyMapRuleWithAccount,
    type MoneyMapWithRulesAndAccounts,
    NEW_CASH_ACCOUNT_OPTION,
} from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { omit } from 'lodash';
import { useEffect, useMemo } from 'react';
import {
    type SubmitHandler,
    useForm,
    type UseFormReturn,
} from 'react-hook-form';
import { Form, USE_FORM_MODE_DEFAULTS, YStack } from 'smoothie';
import { z } from 'zod';

import { selectedMembershipAtom } from '../../../entities/entityAtoms';
import { useMoneyMapAccounts } from '../../providers/MoneyMapAccountsProvider';
import {
    validateAccountNameLength,
    validateIndividualAccountOwnership,
    validatePhysicalCardLimits,
    validateRequiredFields,
} from '../../utils/moneyMapAccountFormUtils';
import { getDefaultAccountSettings } from '../../utils/moneyMapUtils';
import {
    AccountSettingsFormModule,
    accountSettingsFormSchema,
    extractCreateParametersFromAccountSettingsToSubmit,
    type GenericAccountSettingsFormSchema,
} from './AccountSettingsFormModule';
import { AmountFormModule } from './AmountFormModule';
import FormDrawer from './FormDrawer';

interface IncomeFormProps {
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    onPressSave: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<void>;
    onClose?: () => void;
    presentation?: 'drawer' | 'modal';
}

const IncomeForm = ({
    moneyMap,
    onPressSave,
    onClose,
    presentation,
}: IncomeFormProps) => {
    const { accounts } = useMoneyMapAccounts();
    const membership = useAtomValue(selectedMembershipAtom);
    const isSoloMembership = membership?.users?.length === 1;

    const incomeAccount:
        | DeepPartial<MoneyMapAccountWithAccountDetails>
        | undefined = useMemo(() => {
        if (moneyMap?.incomeMoneyMapAccountId != null) {
            return accounts.find(
                (account) => account.id === moneyMap.incomeMoneyMapAccountId,
            );
        }

        return moneyMap.incomeMoneyMapAccount;
    }, [JSON.stringify(moneyMap)]);

    const incomeFormSchema = z
        .object({
            incomeMonthlyAmount: z
                .string()
                .min(3, { message: 'Income monthly amount is required' }), // Account for "$ " prepending formatted dollar amount
            ...accountSettingsFormSchema.shape,
        })
        .superRefine(({ accountSettings }, ctx) => {
            const isNewCashAccount =
                accountSettings.moneyMapAccountId ===
                NEW_CASH_ACCOUNT_OPTION.value;

            const account = accounts.find(
                (account) => account.id === accountSettings.moneyMapAccountId,
            );

            const isProposedCashAccount =
                account?.status === MoneyMapAccountStatus.PROPOSED &&
                account?.type === MoneyMapAccountType.INTERNAL_BANKING;

            const isAccountPhysicalCardValid = validatePhysicalCardLimits(
                'account',
                accounts,
                isSoloMembership,
                accountSettings,
                account,
                ctx,
            );

            const isAccountOwnershipValid =
                accountSettings.ownership === AccountOwnership.INDIVIDUAL
                    ? validateIndividualAccountOwnership(
                          'account',
                          accountSettings.primaryUserId,
                          ctx,
                      )
                    : true;

            const isNewOrProposedCashAccountValid =
                isNewCashAccount || isProposedCashAccount
                    ? validateRequiredFields(
                          'account',
                          [
                              ['name', accountSettings.name],
                              ['ownership', accountSettings.ownership],
                              ['category', accountSettings.category],
                              ['virtualCard', accountSettings.virtualCard],
                              ['physicalCard', accountSettings.physicalCard],
                          ],
                          ctx,
                      ) &&
                      validateAccountNameLength(
                          'account',
                          ctx,
                          accountSettings.name,
                      )
                    : true;

            return (
                isAccountPhysicalCardValid &&
                isAccountOwnershipValid &&
                isNewOrProposedCashAccountValid
            );
        });

    type IncomeFormSchema = z.infer<typeof incomeFormSchema>;

    const formFunctions = useForm<IncomeFormSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(incomeFormSchema),
        defaultValues: {
            incomeMonthlyAmount:
                moneyMap?.monthlyIncomeCents != null
                    ? centsToFormattedDollars(
                          moneyMap.monthlyIncomeCents,
                          false,
                      )
                    : '',
            accountSettings: getDefaultAccountSettings(incomeAccount),
        },
    });

    const selectedMoneyMapAccountId = formFunctions.watch(
        'accountSettings.moneyMapAccountId',
    );

    const selectedMoneyMapAccount = useMemo(() => {
        return accounts.find(
            (account) => account.id === selectedMoneyMapAccountId,
        );
    }, [JSON.stringify(accounts), selectedMoneyMapAccountId]);

    const onSubmit: SubmitHandler<IncomeFormSchema> = async ({
        incomeMonthlyAmount,
        accountSettings,
    }) => {
        if (accountSettings.moneyMapAccountId == null) return;

        const proposedAccountToUpdate = accounts.find(
            (account) =>
                account.id === accountSettings.moneyMapAccountId &&
                account.status === MoneyMapAccountStatus.PROPOSED,
        );

        const updates: DeepPartial<MoneyMapWithRulesAndAccounts> = {
            monthlyIncomeCents: formattedDollarsToCents(incomeMonthlyAmount),
        };

        const isNewCashAccount =
            accountSettings.moneyMapAccountId === NEW_CASH_ACCOUNT_OPTION.value;

        // TODO - Merge with buildMoneyMapAccountForMoneyMapRuleUpsertDto.

        if (isNewCashAccount) {
            // Creating a new proposed money map account
            updates.incomeMoneyMapAccount = {
                membershipId: membership?.id as string,
                type: MoneyMapAccountType.INTERNAL_BANKING,
                status: MoneyMapAccountStatus.PROPOSED,
                createParameters:
                    extractCreateParametersFromAccountSettingsToSubmit(
                        { accountSettings },
                        MoneyMapCategory.INCOME,
                        MoneyMapAccountType.INTERNAL_BANKING,
                    ),
            };
        } else if (proposedAccountToUpdate != null) {
            // Updating existing proposed account
            updates.incomeMoneyMapAccount = {
                ...proposedAccountToUpdate,
                createParameters:
                    extractCreateParametersFromAccountSettingsToSubmit(
                        { accountSettings },
                        MoneyMapCategory.INCOME,
                        // We only ever allow proposing banking accounts for income, this cast should be okay
                        proposedAccountToUpdate.type as MoneyMapAccountType.INTERNAL_BANKING,
                    ),
            };
        } else if (accountSettings.moneyMapAccountId != null) {
            // Selecting an existing money map account. Explicitly set incomeMoneyMapAccount to undefined persistMoneyMapUpdatesWithUpsert overrides any
            // stale embedded account object left over from a prior "propose
            // new account" save — otherwise cleanAttributesForUpsert prefers
            // that stale object over this freshly-selected id.
            updates.incomeMoneyMapAccount = undefined;
            updates.incomeMoneyMapAccountId = accountSettings.moneyMapAccountId;
        }

        // If the income account is not a Fruitful banking account, update any rules' accounts to not have transfer account.
        let rulesPatch: Array<DeepPartial<MoneyMapRuleWithAccount>> | undefined;
        if (!isFruitfulAccount(selectedMoneyMapAccount)) {
            const prunedRules = removeTransferAccountsFromRulesAccounts(
                moneyMap.moneyMapRules ?? [],
            );
            rulesPatch = prunedRules.map((rule) => {
                return {
                    ...rule,
                    moneyMapAccount: omit(
                        rule.moneyMapAccount,
                        'externalAccount',
                    ),
                };
            });
        }

        if (
            updates.incomeMoneyMapAccount != null &&
            proposedAccountToUpdate != null
        ) {
            const accountId = proposedAccountToUpdate.id;
            const baseRules = rulesPatch ?? moneyMap.moneyMapRules ?? [];
            rulesPatch = syncIncomeMoneyMapAccountToRulesSharingAccountId(
                baseRules,
                updates.incomeMoneyMapAccount,
                accountId,
            );
        }

        await onPressSave({
            ...updates,
            ...(rulesPatch != null && { moneyMapRules: rulesPatch }),
        });
    };

    const handleSubmit = formFunctions.handleSubmit;
    const isValid = formFunctions.formState.isValid;

    // Trigger form validation.
    useEffect(() => {
        const subscription = formFunctions.watch(() => {
            void formFunctions.trigger(); // Re-run validation including superRefine
        });

        // Initial trigger for selected account IDs
        void formFunctions.trigger();

        return () => {
            subscription.unsubscribe();
        };
    }, [formFunctions, selectedMoneyMapAccountId]);

    return (
        <FormDrawer
            onClose={onClose}
            presentation={presentation}
            onSave={handleSubmit(onSubmit)}
            saveDisabled={!isValid}
        >
            <Form onSubmit={handleSubmit(onSubmit)}>
                <YStack gap={16}>
                    <AmountFormModule
                        title="Income"
                        label="Monthly take-home pay"
                        name="incomeMonthlyAmount"
                        control={formFunctions.control}
                        register={formFunctions.register}
                        errors={formFunctions.formState.errors}
                    />
                    <AccountSettingsFormModule
                        type="account"
                        accounts={accounts}
                        selectedAccount={selectedMoneyMapAccount}
                        formFunctions={
                            formFunctions as never as UseFormReturn<GenericAccountSettingsFormSchema>
                        }
                        isUsingPreset={moneyMap.isAIGenerated === true}
                    />
                </YStack>
            </Form>
        </FormDrawer>
    );
};

export default IncomeForm;
